import React, {useEffect, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {Trans} from '@lingui/macro'
import {Alert} from 'components/UI/Alert/Alert'
import {
    MinusSmIcon,
    PlusIcon,
    ShoppingCartIcon,
    CalculatorIcon,
    ViewGridIcon
} from '@heroicons/react/outline'
import {Button} from 'components/UI/Button/Button'
import {
    IdPosConfigurationState,
} from 'model/dashboardPosConfiguration/dPosConfigurationReducer'
import {dPosConfigurationGetFullItemSagaAC} from 'model/dashboardPosConfiguration/dPosConfigurationActionCreators'
import {
    getProductListByIds,
} from 'model/dashboardPosConfiguration/dPosConfigurationAPI'
import {logoutSagaAC} from 'model/system/systemActionCreators'




// Type
export type typePopupDetails = {
    setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>
    openPopupEdit: React.Dispatch<React.SetStateAction<boolean>>
    openDeleteDialog: React.Dispatch<React.SetStateAction<boolean>>
}

type typeCategoryItem = {
    selected: boolean,
    id: string,
    name: string,
}

type typeProductItem = {
    id: string,
    name: string,
}

// Component
export const PopupDetails: React.FC<typePopupDetails> = ({setOpenPopup, openPopupEdit, openDeleteDialog}) => {

    const dispatch = useDispatch()

    // System data
    const dataObject = useSelector((state: {dPosConfiguration: IdPosConfigurationState}) => state.dPosConfiguration.detailItem)
    const fullDataObject = useSelector((state: {dPosConfiguration: IdPosConfigurationState}) => state.dPosConfiguration.fullDetailItem)

    useEffect(() => {
        dispatch(dPosConfigurationGetFullItemSagaAC(dataObject!.id))
        // eslint-disable-next-line
    },[])

    // Product list
    const [productList, setProductList] = useState<typeProductItem[]>([])
    const [categoryList, setCategoryList] = useState<typeCategoryItem[]>([])

    useEffect(() => {
        if (fullDataObject) {
            setCategoryList(fullDataObject.configuration.posCategories.map(item => ({id: item.id, name: item.name, selected: false})))
        }
    }, [fullDataObject])

    const onCategorySelect = async (categoryId: string) => {
        const newCategoryList = categoryList.map((item) => item.id === categoryId && !item.selected ? {...item, ...{selected: true}} : {...item, ...{selected: false}})
        setCategoryList(newCategoryList)
        setProductList([])

        const productIds = fullDataObject?.configuration.posCategories.find(item => item.id === categoryId)?.productIds
        let responseProducts = await getProductListByIds({ ids: productIds})

        if (responseProducts.status === 200) {
            const newTempProductList = responseProducts.data
                .map((item: any) => ({id: item.id, name: item.name}))
            setProductList(newTempProductList)
        } else if (responseProducts.status === 401) {
            dispatch(logoutSagaAC())
        }
    }

    return(
        <>
            {dataObject
                ?  <>
                    <div className="grid gap-x-8 grid-cols-1 border-b sm:border sm:rounded-md sm:px-4">

                        <div className="flex flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-0">
                            <div className="text-gray-500 text-left"><Trans>Configuration name:</Trans></div>
                            <div className="text-gray-90 text-left font-medium break-all">{dataObject.name}</div>
                        </div>

                    </div>

                    <div className="grid gap-x-8 grid-cols-1 sm:grid-cols-2">

                        {dataObject.storeIds.length || dataObject.posIds.length
                            ? <div className={dataObject.posCategories.length ? undefined : 'col-span-2'}>

                                { dataObject.storeIds.length
                                    ? <>
                                        <div className="mt-4 flex gap-x-2 items-center">
                                            <ShoppingCartIcon className="h-5 w-5 text-gray-500"/>
                                            <span className="font-medium"><Trans>Stores</Trans></span>
                                        </div>
                                        {fullDataObject && fullDataObject?.store
                                            ? <div className="mt-1 border-t border rounded-md overscroll-y-auto flex flex-col divide-y max-h-40">
                                                {fullDataObject?.store.map((item, index) => <span key={index} className="py-1 px-3 break-all">{item.name}</span>)}
                                            </div>
                                            : <span className="text-gray-400 text-xxs"><Trans>Loading...</Trans></span>
                                        }
                                    </>

                                    : null
                                }

                                { dataObject.posIds.length
                                    ? <>
                                        <div className="mt-4 flex gap-x-2 items-center">
                                            <CalculatorIcon className="h-5 w-5 text-gray-500"/>
                                            <span className="font-medium"><Trans>POS terminals</Trans></span>
                                        </div>
                                        {fullDataObject && fullDataObject?.pos
                                            ? <div className="mt-1 border-t border rounded-md overscroll-y-auto flex flex-col divide-y max-h-40">
                                                {fullDataObject?.pos.map((item, index) => <span key={index} className="py-1 px-3 break-all">{item.posHardwareSerialNumber}</span>)}
                                            </div>
                                            : <span className="text-gray-400 text-xxs"><Trans>Loading...</Trans></span>
                                        }
                                    </>

                                    : null
                                }

                            </div>
                            : null
                        }


                        {fullDataObject && fullDataObject.configuration.posCategories.length
                            ? <div className={dataObject.storeIds.length || dataObject.posIds.length ? undefined : 'col-span-2'}>
                                <div className="mt-4 flex gap-x-2 items-center">
                                    <ViewGridIcon className="h-5 w-5 text-gray-500"/>
                                    <span className="font-medium"><Trans>Categories</Trans></span>
                                </div>
                                <div className="mt-1 flex flex-col border rounded-md overscroll-y-auto h-80">
                                    {categoryList.map((item, index) => <div  key={index}>
                                        <div className={`px-3 py-2 flex items-center gap-x-1 cursor-pointer border-b hover:bg-gray-50${item.selected ? ' bg-gray-50' : ''}`} onClick={() => onCategorySelect(item.id)}>
                                            <span className="flex-grow break-all">{item.name}</span>
                                            {item.selected
                                                ? <MinusSmIcon className="h-5 w-5 text-gray-500"/>
                                                : <PlusIcon className="h-5 w-5 text-gray-500"/>
                                            }
                                        </div>
                                        {item.selected
                                            ?   <div className="border-b flex flex-col divide-y">
                                                {productList.length
                                                    ? productList.map((item, index) => <div key={index} className="py-1 px-3 text-sm flex gap-x-2">
                                                        <span className="text-gray-400">{index + 1}</span>
                                                        <span className={'break-all'}>{item.name}</span>
                                                    </div>)
                                                    : <span className="block text-xs text-gray-400 py-2 px-3 text-center"><Trans>This product group is empty.</Trans></span>
                                                }
                                            </div>
                                            : null
                                        }
                                    </div>)}
                                </div>
                            </div>
                            : null
                        }

                    </div>

                <div className="flex flex-wrap gap-x-4 gap-y-2 md:justify-end">
                    <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <Button type="button" label={<Trans>Edit</Trans>} onClick={() => {setOpenPopup(false); openPopupEdit(true)}}/>
                    </div>
                    <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <Button type="button" label={<Trans>Delete</Trans>} onClick={() => {setOpenPopup(false); openDeleteDialog(true)}}/>
                    </div>
                    <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                        <Button type="button" secondary={true} label={<Trans>Close</Trans>} onClick={() => setOpenPopup(false)}/>
                    </div>
                </div>

                </>
                : <Alert type="error" title={<Trans>Have no data.</Trans>}/>
            }
        </>
    )
}
